import { Vec3 } from '../../engine/math/vec3';
import type { World } from '../World';
import type { ItemId } from '../items/ItemDefs';
import type { ItemLoc } from '../items/Items';
import type { Mode } from '../World';

/** Autosave (§0): world state is written to localStorage on each biome transition and on win. */
export interface SaveData {
  version: number;
  mode: Mode;
  time: number;
  deaths: number;
  foundSecret: boolean;
  player: { pos: [number, number, number]; facing: number; hearts: number };
  items: { id: ItemId; pos: [number, number, number]; loc: ItemLoc; placedAt: string | null; lit: boolean; locked: boolean; touched?: boolean }[];
  keeps: boolean[];
  beasts: { kind: string; hp: number; alive: boolean; pos: [number, number, number] }[];
  magpie: { pos: [number, number, number]; carrying: string | null };
  shuffle: Record<string, string> | null;
}

const KEY = 'threshold.save.v1';

export function hasSave(): boolean {
  try { return !!localStorage.getItem(KEY); } catch { return false; }
}
export function loadSave(): SaveData | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as SaveData;
    return d.version === 1 ? d : null;
  } catch { return null; }
}
export function clearSave(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

export function writeSave(w: World, elapsed: number): void {
  const p = w.player;
  const data: SaveData = {
    version: 1,
    mode: w.mode,
    time: elapsed,
    deaths: w.deaths,
    foundSecret: w.foundSecret,
    player: { pos: [p.position.x, p.position.y, p.position.z], facing: p.facing, hearts: p.hearts },
    items: w.items.serialize(),
    keeps: w.keeps.map((k) => k.open),
    beasts: w.beasts.map((b) => b.serialize()),
    magpie: w.magpie.serialize(),
    shuffle: w.shuffle,
  };
  try { localStorage.setItem(KEY, JSON.stringify(data)); } catch { /* storage full or disabled */ }
}

/** Apply a save to a freshly built world (same mode/shuffle). */
export function applySave(w: World, d: SaveData): void {
  const p = w.player;
  p.teleport(new Vec3(d.player.pos[0], d.player.pos[1], d.player.pos[2]), d.player.facing);
  p.hearts = d.player.hearts;
  w.deaths = d.deaths;
  w.foundSecret = d.foundSecret;
  w.keeps.forEach((k, i) => { if (d.keeps[i]) w.forceOpen(k); });
  w.beasts.forEach((b, i) => { const s = d.beasts[i]; if (s && s.kind === b.kind) b.restore(s); });
  for (const s of d.items) {
    const it = w.items.get(s.id);
    if (!it) continue;
    const pos = new Vec3(s.pos[0], s.pos[1], s.pos[2]);
    it.lit = s.lit;
    it.locked = s.locked;
    it.touched = !!s.touched;
    if (s.id === 'lantern') it.rd.emissiveMul = s.lit ? 1 : 0.15;
    switch (s.loc) {
      case 'held': w.items.release(it, pos); w.items.pickup(it); break;
      case 'placed': { const g = w.gaps.find((x) => x.id === s.placedAt); if (g) w.placePlankAt(it, g); else w.items.release(it, pos); break; }
      case 'magpie': w.items.release(it, pos); w.magpie.grabNow(it, w.items); break;
      case 'buried': it.loc = 'buried'; it.rd.visible = false; it.t.position.copy(pos); it.t.snapPrev(); break;
      case 'caged': it.loc = 'caged'; it.locked = true; it.t.position.copy(pos); it.t.snapPrev(); break;
      default: w.items.release(it, pos); it.sleeping = true; it.vel.set(0, 0, 0); it.t.position.copy(pos); it.t.snapPrev(); break;
    }
  }
  w.magpie.position.set(d.magpie.pos[0], d.magpie.pos[1], d.magpie.pos[2]);
  w.magpie.root.snapPrev();
}
